import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleFilled, ClockCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { wizardLinks } from '../wizardLinks';
import { gradientPrimary, tint, wz, wzType } from '../wizardStyles';
import { CampaignWizardIntent, CampaignWizardResult } from '../wizardSubmission';

const TR = `${TOrbit}.wizard.result`;

const Banner = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 40px 31px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
  text-align: center;
`;

const Icon = styled.span<{ $pending: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: ${({ $pending }) => tint($pending ? wz.highlight : wz.success, 0.14)};
  color: ${({ $pending }) => ($pending ? wz.highlight : wz.success)};
  font-size: 22px;
`;

const Title = styled.h1`
  margin: 14px 0 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.title}
`;

const Body = styled.p`
  margin: 6px 0 0;
  color: ${wz.secondary};
  ${wzType.body}
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 24px;

  > * {
    margin: 0 6px 8px;
  }
`;

const action = `
  display: inline-flex;
  align-items: center;
  min-height: 40.6px;
  padding: 12px 22px;
  border-radius: 9px;
  font-weight: 700;
  ${wzType.input}
`;

const PrimaryLink = styled(Link)`
  ${action}
  background: ${gradientPrimary};
  color: #ffffff;

  &:hover {
    color: #ffffff;
  }
`;

const SecondaryLink = styled(Link)`
  ${action}
  border: ${wz.line} solid ${wz.border};
  background: ${wz.field};
  color: ${wz.text};

  &:hover {
    color: ${wz.text};
  }
`;

const SecondaryAction = styled.button`
  ${action}
  border: ${wz.line} solid ${wz.border};
  background: ${wz.field};
  color: ${wz.text};
  cursor: pointer;
`;

interface IResultViewProps {
  name: string;
  intent: CampaignWizardIntent;
  result: CampaignWizardResult;
  onCreateAnother: () => void;
  onExit: () => void;
}

/** After a save: the campaign's own pages, another campaign, or back to the chat. */
const ResultView: React.FC<IResultViewProps> = ({
  name,
  intent,
  result,
  onCreateAnother,
  onExit
}) => {
  const { t } = useMultiLanguageTranslation();
  const pending = result.requestId !== null;
  const title = pending ? 'pending' : intent === 'draft' ? 'draft' : 'confirmed';
  return (
    <Banner role="status" data-testid="orbit-wizard-result">
      <Icon $pending={pending} aria-hidden="true">
        {pending ? <ClockCircleFilled /> : <CheckCircleFilled />}
      </Icon>
      <Title>{t(`${TR}.${title}`)}</Title>
      <Body>{t(`${TR}.body`, { name, id: result.campaignId })}</Body>
      <Actions>
        {result.requestId !== null ? (
          <PrimaryLink to={wizardLinks.campaignRequest(result.campaignId, result.requestId)}>
            {t(`${TR}.request`)}
          </PrimaryLink>
        ) : (
          <PrimaryLink to={wizardLinks.showCampaign(result.campaignId)}>
            {t(`${TR}.view`)}
          </PrimaryLink>
        )}
        <SecondaryLink to={wizardLinks.editCampaign(result.campaignId)}>
          {t(`${TR}.editCampaign`)}
        </SecondaryLink>
        <SecondaryAction type="button" onClick={onCreateAnother}>
          {t(`${TR}.another`)}
        </SecondaryAction>
        <SecondaryAction type="button" onClick={onExit}>
          {t(`${TOrbit}.wizard.exit`)}
        </SecondaryAction>
      </Actions>
    </Banner>
  );
};

export default ResultView;
